// Apagar um pagamento já lançado no Fechar mesa / Finalizar retirada (dono,
// 24/09/2026: "lancei R$ 20 no PIX sem querer"). Cada pagamento guarda no log
// o que fez nos produtos (`efeito`), e apagar desfaz exatamente isso: tira o
// valor dos produtos, devolve a parte à divisão e reabre a divisão concluída.
// O caixa (estorno da venda) é por conta de quem chama.
import { chaveItem, pagoNaDivisao } from "./divisao";

const centavos = (v) => Math.round((Number(v) || 0) * 100);
const reais = (c) => Math.round(c) / 100;

// Listas que andam junto com `valores` (uma posição por parte)
const PARALELAS = ["valores", "fixas", "descPedPartes", "descManualPartes"];

export const novoIdPagamento = () => `pg${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// PIX Online e PIX pago pelo celular: o dinheiro entrou de verdade (Mercado Pago)
export const ehOnline = (entrada) => !!entrada?.pagamento_id || /pix_online|cartao_online/i.test(`${entrada?.code || ""}`);

/** Só pagamento lançado nesta tela, depois de 24/09/2026 (tem o `efeito`), e não online. */
export const podeApagar = (entrada) => !!entrada?.id && !!entrada?.efeito && !ehOnline(entrada);

/** O que o pagamento fez em cada produto: valor que entrou e se quitou. */
export function efeitoNosItens(antes, depois) {
  const porChave = new Map(antes.map((i) => [String(chaveItem(i)), i]));
  const out = {};
  depois.forEach((item) => {
    const velho = porChave.get(String(chaveItem(item)));
    if (!velho) return;
    const valorC = centavos(item._pagoParcial) - centavos(velho._pagoParcial);
    const quitou = !velho._paid && !!item._paid;
    if (valorC > 0 || quitou) out[String(chaveItem(item))] = { valor: reais(Math.max(0, valorC)), quitou };
  });
  return out;
}

function somarPago(mapa, itens, sinal) {
  const out = { ...mapa };
  Object.entries(itens || {}).forEach(([chave, e]) => {
    out[chave] = reais(Math.max(0, centavos(out[chave]) + sinal * centavos(e.valor)));
  });
  return out;
}

// Tira a parte `j` (paga) e a devolve como a próxima a pagar
function devolverParte(div, j, efeito) {
  const nova = { ...div };
  const pagas = div.pagas - 1;
  PARALELAS.forEach((campo) => {
    if (!Array.isArray(div[campo])) return;
    const lista = [...div[campo]];
    while (lista.length < div.valores.length) lista.push(campo === "fixas" ? false : 0);
    const [v] = lista.splice(j, 1);
    lista.splice(pagas, 0, v);
    nova[campo] = lista;
  });
  return { ...nova, pagas, partes: nova.valores.length, pagoPorItem: somarPago(pagoNaDivisao(div), efeito.itens, -1), pagoPorItemPagas: pagas };
}

// Produto que recebeu parte deste pagamento e depois foi quitado por outro:
// apagar este deixaria o produto verde sem o dinheiro todo
function quitadoDepois(log, pos, efeito, items) {
  for (const [chave, e] of Object.entries(efeito.itens || {})) {
    if (e.quitou) continue;
    const item = items.find((i) => String(chaveItem(i)) === chave);
    if (!item?._paid) continue;
    const quem = log.slice(pos + 1).find((l) => l.efeito?.itens?.[chave]?.quitou);
    const fechaMesmaDivisao = quem && efeito.divisaoId && quem.efeito.divisaoId === efeito.divisaoId && quem.efeito.concluiu;
    if (fechaMesmaDivisao) continue;
    const nome = item.name || item.nome || "produto";
    if (quem && ehOnline(quem)) return `${nome} foi quitado depois pelo ${quem.label || "PIX Online"}. Esse pagamento não pode mais ser apagado.`;
    return quem
      ? `${nome} foi quitado depois por outro pagamento (${quem.label || quem.code}). Apague aquele primeiro.`
      : `${nome} foi quitado depois (pelo celular). Esse pagamento não pode mais ser apagado.`;
  }
  return null;
}

/**
 * Desfaz o pagamento `id` em `{ log, items, divisao }`. Devolve
 * `{ log, items, divisao, efeito, entradas }` ou `{ erro }`. Quem chama
 * desfaz o resto: por valor (`efeito.porValor`), desconto, estorno no caixa.
 */
export function desfazerPagamento({ log, items, divisao }, id) {
  const pos = log.findIndex((l) => l?.id === id);
  const entradas = log.filter((l) => l?.id === id);
  const efeito = entradas.find((l) => l.efeito)?.efeito;
  if (pos < 0 || !efeito || entradas.some(ehOnline)) return { erro: "Esse pagamento não pode ser apagado aqui." };
  // A divisão criada depois deste pagamento descontou o valor dele: apagar
  // com ela ativa faria a divisão quitar produto sem o dinheiro todo
  const naDivisao = divisao && efeito.divisaoId !== divisao.id
    && (efeito.porValor > 0 || Object.keys(efeito.itens || {}).some((k) => k in (divisao.base || {})));
  if (naDivisao || (efeito.porValor > 0 && log.slice(pos + 1).some((l) => l.efeito?.divisaoId))) {
    return { erro: "A divisão foi calculada com esse pagamento. Cancele a divisão (ou apague as partes dela) antes." };
  }
  // "Por valor" e pagamento por produto se apoiam um no outro: o produto
  // marcado cobra só o que falta da mesa (descontando o "por valor"), e o
  // "por valor" que quita a mesa marca os produtos restantes. Apagar o de
  // antes com o outro ainda lá deixaria produto verde sem o dinheiro.
  const depois = log.slice(pos + 1).filter((l) => l?.efeito && l.id !== id);
  const mexeuEmProduto = (e) => Object.keys(e?.itens || {}).length > 0;
  const posterior = depois.find((l) => (efeito.porValor > 0 && mexeuEmProduto(l.efeito))
    || (l.efeito.porValor > 0 && Object.values(l.efeito.itens || {}).some((x) => x.quitou)));
  if (posterior) {
    return ehOnline(posterior)
      ? { erro: `Depois dele entrou o ${posterior.label || "PIX Online"}, que conta com ele. Esse pagamento não pode mais ser apagado.` }
      : { erro: `Depois dele entrou outro pagamento (${posterior.label || posterior.code}) que conta com ele. Apague aquele primeiro.` };
  }
  const bloqueio = quitadoDepois(log, pos, efeito, items);
  if (bloqueio) return { erro: bloqueio };

  let novoLog = log.filter((l) => l?.id !== id);
  let novosItens = items;
  let div = divisao;

  if (efeito.divisaoId) {
    const daDivisao = (l) => l?.efeito?.divisaoId === efeito.divisaoId;
    if (div && div.id !== efeito.divisaoId) return { erro: "Termine ou cancele a divisão atual antes de apagar um pagamento de outra divisão." };
    if (!div) {
      // Divisão já concluída: reabre. O que a última parte quitou volta a ficar em aberto
      const fecho = log.find((l) => daDivisao(l) && l.efeito.concluiu);
      if (fecho) {
        const antes = fecho.efeito.divisaoAntes;
        div = { ...antes, pagas: antes.pagas + 1, pagoPorItem: somarPago(pagoNaDivisao(antes), fecho.efeito.itens, 1), pagoPorItemPagas: antes.pagas + 1 };
        const reabertos = new Set(Object.entries(fecho.efeito.itens || {}).filter(([, e]) => e.quitou).map(([k]) => k));
        novosItens = novosItens.map((i) => (reabertos.has(String(chaveItem(i))) ? { ...i, _paid: false, _paymentInfo: null } : i));
        const semQuitar = Object.fromEntries(Object.entries(fecho.efeito.itens || {}).map(([k, e]) => [k, { ...e, quitou: false }]));
        novoLog = novoLog.map((l) => (l.id === fecho.id && l.efeito ? { ...l, efeito: { ...l.efeito, concluiu: false, itens: semQuitar } } : l));
      }
      // sem fecho: a divisão foi cancelada — só desfaz nos produtos
    }
    if (div) {
      const ordem = [...new Set(log.filter(daDivisao).map((l) => l.id))];
      const j = ordem.indexOf(id);
      if (j < 0 || ordem.length !== div.pagas) return { erro: "Parte de divisão feita antes da atualização: cancele a divisão para corrigir." };
      div = devolverParte(div, j, efeito);
    }
  }

  novosItens = novosItens.map((item) => {
    const chave = String(chaveItem(item));
    const e = efeito.itens?.[chave];
    const hist = item._histParcial || [];
    if (!e && !hist.some((h) => h.pagamento === id)) return item;
    const volta = { ...item, _histParcial: hist.filter((h) => h.pagamento !== id) };
    if (e?.quitou) Object.assign(volta, { _paid: false, _paymentInfo: null });
    if (e?.valor > 0) volta._pagoParcial = reais(Math.max(0, centavos(item._pagoParcial) - centavos(e.valor)));
    return volta;
  });

  return { log: novoLog, items: novosItens, divisao: div, efeito, entradas };
}
